import React, {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useAccommodationTopOffers} from 'components/Accommodation/Offers/useAccommodationTopOffers'
import {scrollToSection} from 'components/ScrollSpy'
import {useSearchBox} from 'hooks/searchBox'
import {SRPSearchFlow} from 'hooks/useGetSRPSearchFlow'
import {useIsSrpSearchSeoFlow} from 'hooks/useIsSrpSearchSeoFlow'
import {getMetaBrandHasRooms} from 'modules/accommodation/selectors'
import {AccommodationPageNavSections} from 'modules/accommodation/types'
import {getLocaleCode} from 'modules/meta/selectors'
import {
  getIsNoDatesSearch,
  getUrlParams as getSearchUrlParams
} from 'modules/search/selectors'
import {
  getIsRoomsQueryLoading,
  getIsRoomsResultEmpty
} from 'modules/searchApi/selectors'
import {getSearchBoxValues} from 'modules/searchBox/selectors'
import {setOpenDatePickerType} from 'modules/searchBox/slice'
import {DatePickerType, SearchTrigger} from 'modules/searchBox/types'
import {generateAccommodationPageURL} from 'utils/url'

import {useRouterProvider} from '@vio/core/src/_web/router/context/RouterContext'
import {getQueryStringObject} from '@vio/core/src/_web/utils/url'

import {useShouldShowHotelCardClickersContent} from '../useShouldShowHotelCardClickersContent'
import {useViewDealsScrollTarget} from '../useViewDealsScrollTarget'
import {SearchForm} from './SearchForm'

const PENDING_SCROLL_TIMEOUT_MS = 10_000

export type UpdateSearch = (updateParams: {
  checkIn: string
  checkOut: string
  rooms: string
  optimizeRooms?: boolean
  searchTrigger: SearchTrigger
}) => void

export const SearchBox = () => {
  const dispatch = useDispatch()
  const isSrpSearchSeoFlow = useIsSrpSearchSeoFlow()
  const shouldShowHotelCardClickersContent =
    useShouldShowHotelCardClickersContent()
  const viewDealsScrollTarget = useViewDealsScrollTarget()
  const {getQueryParams} = useRouterProvider()

  const {hotelId, optimizeRooms} = useSelector(getSearchUrlParams)
  const {checkIn, checkOut, rooms} = useSelector(getSearchBoxValues)

  const isNoDatesSearch = useSelector(getIsNoDatesSearch)

  const locale = useSelector(getLocaleCode)

  useEffect(() => {
    if (hotelId && isNoDatesSearch) {
      dispatch(setOpenDatePickerType(DatePickerType.CheckIn))
    }
  }, [hotelId, isNoDatesSearch, dispatch])

  // A Vio clicker's search drops clickedOffer, so the page switches to the
  // hotel-clicker layout and the content above the rooms changes height while
  // the new results load. Scroll once they're in, since a smooth scroll can't
  // follow a moving target.
  const [isRoomsScrollPending, setIsRoomsScrollPending] = useState(false)
  const {isComplete: areOffersLoaded} = useAccommodationTopOffers({hotelId})
  const brandHasRooms = useSelector(getMetaBrandHasRooms)
  const isRoomsLoading = useSelector(getIsRoomsQueryLoading)
  const isRoomsResultEmpty = useSelector(getIsRoomsResultEmpty)
  const isSearchLoading = !areOffersLoaded || (brandHasRooms && isRoomsLoading)

  useEffect(() => {
    if (!isRoomsScrollPending || isSearchLoading) return
    setIsRoomsScrollPending(false)
    // Without Vio rooms there's no rooms section, so land on the other offers.
    scrollToSection(
      brandHasRooms && !isRoomsResultEmpty
        ? AccommodationPageNavSections.Rooms
        : AccommodationPageNavSections.Overview
    )
  }, [brandHasRooms, isRoomsResultEmpty, isRoomsScrollPending, isSearchLoading])

  // The results can take a while. Don't pull the page away from where the user
  // has scrolled to since, and don't keep the scroll queued if they never load.
  useEffect(() => {
    if (!isRoomsScrollPending) return
    const cancelScroll = () => setIsRoomsScrollPending(false)
    const userScrollEvents = ['wheel', 'touchmove', 'keydown'] as const
    userScrollEvents.forEach(event =>
      window.addEventListener(event, cancelScroll, {passive: true})
    )
    const timeoutId = window.setTimeout(cancelScroll, PENDING_SCROLL_TIMEOUT_MS)

    return () => {
      userScrollEvents.forEach(event =>
        window.removeEventListener(event, cancelScroll)
      )
      window.clearTimeout(timeoutId)
    }
  }, [isRoomsScrollPending])

  const {doSearch} = useSearchBox()

  const updateSearch: UpdateSearch = ({
    checkIn,
    checkOut,
    rooms,
    optimizeRooms,
    searchTrigger
  }) => {
    const srpSearchFlow = getQueryParams()?.srpSearchFlow as SRPSearchFlow

    if (isSrpSearchSeoFlow) {
      // Send users back to anchor hotel SRP
      doSearch({
        newUrlParams: {
          hotelId,
          checkIn,
          checkOut,
          rooms,
          optimizeRooms: optimizeRooms ? 1 : undefined
        },
        searchTrigger,
        searchOptions: {
          method: 'replace',
          state: {}
        }
      })
      return
    }
    const params = getQueryStringObject()
    const redirectId = params?.redirectId as string
    const navigation = doSearch({
      newUrlParams: {
        checkIn,
        checkOut,
        rooms,
        optimizeRooms: optimizeRooms ? 1 : undefined,
        redirectId,
        srpSearchFlow
      },
      searchTrigger,
      searchOptions: {
        basePath: generateAccommodationPageURL(hotelId, locale)
      }
    })

    if (shouldShowHotelCardClickersContent) {
      scrollToSection(viewDealsScrollTarget)
      return
    }

    Promise.resolve(navigation).then(() => setIsRoomsScrollPending(true))
  }

  return (
    <SearchForm
      checkIn={checkIn ?? ''}
      checkOut={checkOut ?? ''}
      rooms={rooms}
      optimizeRooms={optimizeRooms === '1' ? true : undefined}
      showOptimizedRoomsOption={shouldShowHotelCardClickersContent}
      updateSearch={updateSearch}
    />
  )
}
